import React from 'react';

interface ContactDetailProps {
  contactData: ContactDetail;
  onClose?: () => void;
}

interface ContactDetail {
  id: string;
  status: string;
  isSendConfirmMail: boolean;
  formType: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  consent: boolean;
  payload: {
    messageContent?: string;
    estimatedInvestment?: number;
    detailedAddress?: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

const DetailContact: React.FC<ContactDetailProps> = ({
  contactData,
  onClose
}) => {

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('84')) {
      return phone.substring(2);
    }
    return phone;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return { label: 'Đang chờ', color: 'bg-yellow-100 text-yellow-800' };
      case 'contacted': return { label: 'Đã liên hệ', color: 'bg-blue-100 text-blue-800' };
      case 'resolved': return { label: 'Đã giải quyết', color: 'bg-green-100 text-green-800' };
      case 'spam': return { label: 'Spam', color: 'bg-red-100 text-red-800' };
      default: return { label: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getFormTypeLabel = (formType: string) => {
    switch (formType) {
      case 'partnership': return 'Đối tác';
      case 'franchise': return 'Nhượng quyền';
      default: return formType;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Chi tiết liên hệ</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {contactData ? (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Thông tin cơ bản</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Họ tên
                  </label>
                  <p className="text-gray-800 font-medium">{contactData.fullName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Email
                  </label>
                  <p className="text-gray-800">{contactData.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Số điện thoại
                  </label>
                  <p className="text-gray-800">{formatPhoneNumber(contactData.phoneNumber)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Loại form
                  </label>
                  <p className="text-[pink] font-bold">{getFormTypeLabel(contactData.formType)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Trạng thái
                  </label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusLabel(contactData.status).color}`}>
                    {getStatusLabel(contactData.status).label}
                  </span>
                </div>
              </div>
            </div>

            {contactData.formType === 'partnership' && (
              <>
                {contactData.payload?.messageContent && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">Nội dung tin nhắn</h3>
                    <div className="bg-white rounded border p-3">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {contactData.payload.messageContent}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {contactData.formType === 'franchise' && (
              <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">Thông tin nhượng quyền</h3>
                  <div className="space-y-4">
                    {contactData.payload?.estimatedInvestment && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Kinh phí đầu tư dự kiến
                        </label>
                        <p className="text-gray-800 font-medium text-lg">
                          {formatCurrency(contactData.payload.estimatedInvestment)}
                        </p>
                      </div>
                    )}
                    {contactData.payload?.detailedAddress && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Địa chỉ chi tiết
                        </label>
                        <p className="text-gray-800">
                          {contactData.payload.detailedAddress}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {!['partnership', 'franchise'].includes(contactData.formType) && Object.keys(contactData.payload).length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Thông tin khác</h3>
                <div className="space-y-2">
                  {Object.entries(contactData.payload).map(([key, value]) => (
                    <div key={key} className="flex flex-wrap">
                      <span className="font-medium text-gray-600 min-w-[120px]">
                        {key}:
                      </span>
                      <span className="text-gray-800 flex-1">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Không tìm thấy thông tin liên hệ
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[pink] text-white rounded-lg hover:bg-pink-600 transition-colors"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailContact;